import React from 'react';
import { Table } from 'antd';
import { useSelector } from 'react-redux';
import Loader from '../../LoadingSpinner/LoadingSpinner';
import './ProjectTable.scss';

// ✅ Detect Percentage
const isPercentage = (val) => {
  if (val === null || val === undefined) return false;
  return val.toString().includes("%");
};

// ✅ Extract Numeric Value Safely
const getNumericValue = (val) => {
  if (!val) return 0;
  return Number(val.toString().replace(/,/g, "").replace("%", ""));
};

const OperatingPerformanceTable = ({ tableData = [], dates }) => {
        const { loading } = useSelector((state) => state.operating);

        // ✅ Filter out unwanted descriptions
        const filteredData = (tableData || []).filter(
          (row) => {
            const desc = String(row?.DESCRIPTION || "").trim();
            return !["Value Added", "Value Added as a % of Revenue"].includes(desc);
          }
        );

        // ✅ Generate columns dynamically
        const columns = React.useMemo(() => {
          if (!filteredData?.length) return [];

          // ✅ pick first NON-heading row for keys (safer)
          const sampleRow =
            filteredData.find((r) => String(r?.isHeading) !== "1") || filteredData[0];

          const monthKeys = Object.keys(sampleRow || {}).filter((key) => {
            return (
              key.includes("_") &&
              !key.includes("formatted") &&
              key !== "HEAD_ID" &&
              key !== "ACTUAL_LAST_YEAR" &&
              key !== "DESCRIPTION" &&
              key !== "isHeading" &&
              key !== "key"
            );
          });

          const baseColumns = [
            {
              title: "Description",
              dataIndex: "DESCRIPTION",
              key: "DESCRIPTION",
              fixed: "left",
              width: 250,
              render: (text, record) => {
                return <div className="text-wrap">{text || '--'}</div>;
              },
            },

            ...monthKeys.map((key) => ({
              title: key.replace("_", "-"),
              dataIndex: key,
              key,
              align: "right",
              width: 120,
              render: (val, record) => {
                // ✅ heading row -> keep blank space
                if (String(record?.isHeading) === "1") return "\u00A0";

                if (isPercentage(val)) return val;

                const numericValue = getNumericValue(val);

                return numericValue.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              },
            })),

            // ✅ Total column
            {
              title: "Total",
              key: "row_total",
              align: "right",
              width: 150,
              fixed: "right",
              render: (_, record) => {
                // ✅ heading row -> blank space
                if (String(record?.isHeading) === "1") return "\u00A0";

                const total = monthKeys.reduce((sum, key) => {
                  const value = record[key];
                  if (isPercentage(value)) return sum; // Skip %
                  return sum + getNumericValue(value);
                }, 0);

                return total.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              },
            },
          ];

          return baseColumns;
        }, [filteredData]);

        // ✅ Process table data
        const processedData = React.useMemo(() => {
          const modifiedTableData = [];
          
          filteredData?.forEach((row) => {
            if (
              row.DESCRIPTION === 'Amount considered as Conversion Cost' ||
              row.DESCRIPTION === 'Amount considered as Conversion cost'
            ) {
              modifiedTableData.push({ isConversionSpacer: true }); 
            }
            modifiedTableData.push(row);
          });

          return modifiedTableData.map((item, index) => ({
            ...item,
            key: item.ID || item.DESCRIPTION || index,
          }));
        }, [filteredData]);

        return (
                <>
                        {loading ? (
                                <Loader />
                        ) : (
                                <Table
                                        columns={columns}
                                        dataSource={processedData}
                                        pagination={false}
                                        bordered
                                        className="custom-ant-table"
                                        scroll={{ x: 'max-content', y: 400 }}
                                        rowClassName={(record, index) => {
                                                if (record?.isConversionSpacer) {
                                                        return 'table-row-conversion-cost'; // 👈 New style
                                                } else if (record?.isHeading == '1') {
                                                        return 'table-row-heading';
                                                } else if (record?.isHeading == '2') {
                                                        return 'table-row-light-blue';
                                                }
                                                return '';
                                        }}
                                />
                        )}
                </>
        );
};

export default OperatingPerformanceTable;
